import {
  AlertCircle,
  CheckCircle,
  Clock,
  Phone,
  PhoneCall,
  Save,
  Trash2,
} from 'lucide-react-native';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Switch } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useFunnelTemplates } from '../../../hooks/useLeads';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import {
  useAutoCallRule,
  useCreateAutoCallRule,
  useDeleteAutoCallRule,
  useUpdateAutoCallRule,
  useVoiceTemplates,
} from '../hooks/useVoice';
import type { AutoCallTriggerEvent, VoiceTemplate } from '../types';

const MAX_AUTO_CALL_RETRIES = 2;

interface AutoCallSettingsProps {
  organizationId: string;
}

const TRIGGER_EVENT_OPTIONS: Array<{ value: AutoCallTriggerEvent; label: string; desc: string }> = [
  { value: 'lead_created', label: 'Lead Created', desc: 'Call when a new lead is added' },
  {
    value: 'lead_stage_changed',
    label: 'Stage Changed',
    desc: 'Call when lead moves to a specific stage',
  },
  { value: 'lead_assigned', label: 'Lead Assigned', desc: 'Call when lead is assigned to a rep' },
];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function SectionCard({
  title,
  icon,
  children,
}: Readonly<{ title: string; icon: ReactNode; children: ReactNode }>) {
  return (
    <Box
      backgroundColor={colors.background.card}
      borderRadius="lg"
      borderWidth={1}
      borderColor={colors.border.muted}
      padding="lg"
    >
      <VStack spacing="md">
        <HStack spacing="sm" align="center">
          {icon}
          <Text variant="body" color={colors.text.primary} style={{ fontWeight: '600' }}>
            {title}
          </Text>
        </HStack>
        {children}
      </VStack>
    </Box>
  );
}

function TemplateOption({
  template,
  selected,
  onSelect,
}: Readonly<{ template: VoiceTemplate; selected: boolean; onSelect: () => void }>) {
  return (
    <Pressable onPress={onSelect}>
      <Box
        backgroundColor={selected ? colors.primary.muted : colors.background.input}
        borderRadius="md"
        borderWidth={1}
        borderColor={selected ? colors.primary.main : colors.border.input}
        padding="md"
      >
        <HStack justify="between" align="center">
          <VStack spacing="xxs" style={{ flex: 1 }}>
            <Text
              variant="body"
              color={colors.text.primary}
              style={{ fontWeight: selected ? '600' : '400' }}
            >
              {template.name}
            </Text>
            {template.description ? (
              <Text variant="caption" color={colors.text.tertiary} numberOfLines={1}>
                {template.description}
              </Text>
            ) : null}
          </VStack>
          {selected && <CheckCircle size={18} color={colors.primary.main} />}
        </HStack>
      </Box>
    </Pressable>
  );
}

function StageChip({
  name,
  stageColor,
  selected,
  onToggle,
}: Readonly<{ name: string; stageColor: string; selected: boolean; onToggle: () => void }>) {
  return (
    <Pressable onPress={onToggle}>
      <Box
        backgroundColor={selected ? colors.primary.muted : colors.background.input}
        borderRadius="full"
        borderWidth={1}
        borderColor={selected ? colors.primary.main : colors.border.input}
        paddingX="md"
        paddingY="sm"
      >
        <HStack spacing="xs" align="center">
          <Box
            style={{
              width: 10,
              height: 10,
              borderRadius: 5,
              backgroundColor: stageColor,
            }}
          />
          <Text
            variant="caption"
            color={selected ? colors.primary.main : colors.text.secondary}
            style={{ fontWeight: selected ? '600' : '400' }}
          >
            {name}
          </Text>
        </HStack>
      </Box>
    </Pressable>
  );
}

export function AutoCallSettings({ organizationId }: Readonly<AutoCallSettingsProps>) {
  const { data: rule, isLoading: isLoadingRule } = useAutoCallRule(organizationId);
  const { data: voiceTemplates, isLoading: isLoadingTemplates } = useVoiceTemplates(organizationId);
  const { data: funnelTemplates } = useFunnelTemplates(organizationId);

  const createRuleMutation = useCreateAutoCallRule();
  const updateRuleMutation = useUpdateAutoCallRule();
  const deleteRuleMutation = useDeleteAutoCallRule();

  const [isActive, setIsActive] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [triggerEvents, setTriggerEvents] = useState<AutoCallTriggerEvent[]>(['lead_created']);
  const [triggerStageIds, setTriggerStageIds] = useState<string[]>([]);
  const [delayMinutes, setDelayMinutes] = useState(5);
  const [maxCallsPerDay, setMaxCallsPerDay] = useState(50);
  const [maxRetries, setMaxRetries] = useState(2);
  const [businessDays, setBusinessDays] = useState([1, 2, 3, 4, 5]);
  const [businessHoursStart, setBusinessHoursStart] = useState('09:00');
  const [businessHoursEnd, setBusinessHoursEnd] = useState('18:00');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (rule) {
      setIsActive(rule.isActive);
      setSelectedTemplateId(rule.voiceTemplateId);
      setTriggerEvents(rule.triggerEvents);
      setTriggerStageIds(rule.triggerStageIds);
      setDelayMinutes(rule.delayMinutes);
      setMaxCallsPerDay(rule.maxCallsPerDay);
      setMaxRetries(Math.min(rule.maxRetries, MAX_AUTO_CALL_RETRIES));
      setBusinessDays(rule.businessDays);
      setBusinessHoursStart(rule.businessHoursStart);
      setBusinessHoursEnd(rule.businessHoursEnd);
    }
  }, [rule]);

  const activeTemplate = funnelTemplates?.find((t) => t.isActive);
  const stages = activeTemplate?.stages || [];

  const toggleTriggerEvent = useCallback((event: AutoCallTriggerEvent) => {
    setTriggerEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
    );
    setHasChanges(true);
  }, []);

  const toggleStageId = useCallback((stageId: string) => {
    setTriggerStageIds((prev) =>
      prev.includes(stageId) ? prev.filter((id) => id !== stageId) : [...prev, stageId],
    );
    setHasChanges(true);
  }, []);

  const toggleBusinessDay = useCallback((day: number) => {
    setBusinessDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort(),
    );
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedTemplateId) return;

    const payload = {
      voiceTemplateId: selectedTemplateId,
      isActive,
      triggerEvents,
      triggerStageIds,
      delayMinutes,
      maxCallsPerDay,
      maxRetries,
      businessDays,
      businessHoursStart,
      businessHoursEnd,
    };

    if (rule) {
      await updateRuleMutation.mutateAsync({
        organizationId,
        input: payload,
      });
    } else {
      await createRuleMutation.mutateAsync({
        organizationId,
        ...payload,
      });
    }
    setHasChanges(false);
  }, [
    rule,
    organizationId,
    selectedTemplateId,
    isActive,
    triggerEvents,
    triggerStageIds,
    delayMinutes,
    maxCallsPerDay,
    maxRetries,
    businessDays,
    businessHoursStart,
    businessHoursEnd,
    createRuleMutation,
    updateRuleMutation,
  ]);

  const handleDelete = useCallback(async () => {
    await deleteRuleMutation.mutateAsync(organizationId);
  }, [organizationId, deleteRuleMutation]);

  const isSaving = createRuleMutation.isPending || updateRuleMutation.isPending;
  const showStageSelector = triggerEvents.includes('lead_stage_changed');

  const numberOptions = useCallback(
    (current: number, options: number[], onChange: (v: number) => void) => (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: tokens.spacing.sm }}
      >
        {options.map((opt) => (
          <Pressable
            key={opt}
            onPress={() => {
              onChange(opt);
              setHasChanges(true);
            }}
          >
            <Box
              backgroundColor={current === opt ? colors.primary.subtle : colors.background.input}
              borderRadius="md"
              borderWidth={1}
              borderColor={current === opt ? colors.primary.main : colors.border.input}
              paddingX="md"
              paddingY="sm"
              style={{ minWidth: 48, alignItems: 'center' }}
            >
              <Text
                variant="caption"
                color={current === opt ? colors.primary.main : colors.text.secondary}
                style={{ fontWeight: current === opt ? '600' : '400' }}
              >
                {opt}
              </Text>
            </Box>
          </Pressable>
        ))}
      </ScrollView>
    ),
    [],
  );

  const delayOptions = useMemo(() => [0, 1, 2, 5, 10, 15, 30, 60], []);
  const maxCallOptions = useMemo(() => [10, 25, 50, 100, 200, 500], []);
  const retryOptions = useMemo(
    () => Array.from({ length: MAX_AUTO_CALL_RETRIES + 1 }, (_, retries) => retries),
    [],
  );

  if (isLoadingRule || isLoadingTemplates) {
    return (
      <Box flex={1} alignItems="center" padding="xl">
        <ActivityIndicator size="small" color={colors.primary.main} />
      </Box>
    );
  }

  if (!voiceTemplates || voiceTemplates.length === 0) {
    return (
      <Box
        backgroundColor={colors.background.card}
        borderRadius="lg"
        borderWidth={1}
        borderColor={colors.border.muted}
        padding="xl"
        alignItems="center"
      >
        <VStack spacing="md" style={{ alignItems: 'center' }}>
          <AlertCircle size={36} color={colors.text.tertiary} />
          <Text variant="body" color={colors.text.secondary} style={{ textAlign: 'center' }}>
            Create a voice template first before configuring auto-call settings.
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <VStack spacing="lg">
      <HStack justify="between" align="center">
        <HStack spacing="sm" align="center">
          <Text variant="body" color={colors.text.secondary}>
            Auto-call is
          </Text>
          <Box
            backgroundColor={isActive ? colors.status.success.bg : colors.surface.row}
            borderRadius="full"
            paddingX="sm"
            paddingY="xxs"
          >
            <Text
              variant="caption"
              color={isActive ? colors.status.success.main : colors.text.tertiary}
              style={{ fontWeight: '600' }}
            >
              {isActive ? 'ACTIVE' : 'INACTIVE'}
            </Text>
          </Box>
        </HStack>
        <Switch
          value={isActive}
          onValueChange={(v) => {
            setIsActive(v);
            setHasChanges(true);
          }}
          trackColor={{ false: colors.background.tertiary, true: colors.primary.main }}
        />
      </HStack>

      <SectionCard title="Voice Template" icon={<Phone size={16} color={colors.primary.main} />}>
        <Text variant="caption" color={colors.text.tertiary}>
          Select which voice template to use for auto-calls
        </Text>
        <VStack spacing="sm">
          {voiceTemplates.map((template) => (
            <TemplateOption
              key={template.id}
              template={template}
              selected={selectedTemplateId === template.id}
              onSelect={() => {
                setSelectedTemplateId(template.id);
                setHasChanges(true);
              }}
            />
          ))}
        </VStack>
      </SectionCard>

      <SectionCard
        title="Trigger Events"
        icon={<PhoneCall size={16} color={colors.primary.main} />}
      >
        <Text variant="caption" color={colors.text.tertiary}>
          When should auto-calls be triggered?
        </Text>
        <VStack spacing="sm">
          {TRIGGER_EVENT_OPTIONS.map((opt) => {
            const selected = triggerEvents.includes(opt.value);
            return (
              <Pressable key={opt.value} onPress={() => toggleTriggerEvent(opt.value)}>
                <Box
                  backgroundColor={selected ? colors.primary.muted : colors.background.input}
                  borderRadius="md"
                  borderWidth={1}
                  borderColor={selected ? colors.primary.main : colors.border.input}
                  padding="md"
                >
                  <HStack justify="between" align="center">
                    <VStack spacing="xxs" style={{ flex: 1 }}>
                      <Text
                        variant="body"
                        color={colors.text.primary}
                        style={{ fontWeight: selected ? '600' : '400' }}
                      >
                        {opt.label}
                      </Text>
                      <Text variant="caption" color={colors.text.tertiary}>
                        {opt.desc}
                      </Text>
                    </VStack>
                    {selected && <CheckCircle size={18} color={colors.primary.main} />}
                  </HStack>
                </Box>
              </Pressable>
            );
          })}
        </VStack>

        {showStageSelector && stages.length > 0 && (
          <VStack spacing="sm">
            <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '500' }}>
              Trigger on these stages:
            </Text>
            <HStack style={{ flexWrap: 'wrap', gap: tokens.spacing.sm }}>
              {stages.map((stage) => (
                <StageChip
                  key={stage.id}
                  name={stage.name}
                  stageColor={stage.color}
                  selected={triggerStageIds.includes(stage.id)}
                  onToggle={() => toggleStageId(stage.id)}
                />
              ))}
            </HStack>
            {triggerStageIds.length === 0 && (
              <Text variant="caption" color={colors.accent.amber[400]}>
                No stages selected — calls will trigger on any stage change
              </Text>
            )}
          </VStack>
        )}
      </SectionCard>

      <SectionCard title="Call Settings" icon={<Clock size={16} color={colors.primary.main} />}>
        <VStack spacing="lg">
          <VStack spacing="sm">
            <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '500' }}>
              Delay before calling (minutes)
            </Text>
            {numberOptions(delayMinutes, delayOptions, setDelayMinutes)}
          </VStack>

          <VStack spacing="sm">
            <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '500' }}>
              Max calls per day
            </Text>
            {numberOptions(maxCallsPerDay, maxCallOptions, setMaxCallsPerDay)}
          </VStack>

          <VStack spacing="sm">
            <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '500' }}>
              Max retries on no-answer
            </Text>
            {numberOptions(maxRetries, retryOptions, setMaxRetries)}
          </VStack>

          <VStack spacing="sm">
            <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '500' }}>
              Business days
            </Text>
            <HStack style={{ flexWrap: 'wrap', gap: tokens.spacing.sm }}>
              {DAY_LABELS.map((label, idx) => {
                const selected = businessDays.includes(idx);
                return (
                  <Pressable key={label} onPress={() => toggleBusinessDay(idx)}>
                    <Box
                      backgroundColor={selected ? colors.primary.subtle : colors.background.input}
                      borderRadius="md"
                      borderWidth={1}
                      borderColor={selected ? colors.primary.main : colors.border.input}
                      style={styles.dayChip}
                    >
                      <Text
                        variant="caption"
                        color={selected ? colors.primary.main : colors.text.tertiary}
                        style={{ fontWeight: selected ? '600' : '400' }}
                      >
                        {label}
                      </Text>
                    </Box>
                  </Pressable>
                );
              })}
            </HStack>
          </VStack>

          <HStack spacing="lg">
            <VStack spacing="xs" style={{ flex: 1 }}>
              <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '500' }}>
                Start
              </Text>
              <Box
                backgroundColor={colors.background.input}
                borderRadius="md"
                borderWidth={1}
                borderColor={colors.border.input}
                padding="md"
              >
                <Text variant="body" color={colors.text.primary}>
                  {businessHoursStart}
                </Text>
              </Box>
            </VStack>
            <VStack spacing="xs" style={{ flex: 1 }}>
              <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '500' }}>
                End
              </Text>
              <Box
                backgroundColor={colors.background.input}
                borderRadius="md"
                borderWidth={1}
                borderColor={colors.border.input}
                padding="md"
              >
                <Text variant="body" color={colors.text.primary}>
                  {businessHoursEnd}
                </Text>
              </Box>
            </VStack>
          </HStack>
        </VStack>
      </SectionCard>

      <HStack spacing="md" justify="between">
        {rule && (
          <Button variant="ghost" onPress={handleDelete} disabled={deleteRuleMutation.isPending}>
            <HStack spacing="sm" align="center">
              <Trash2 size={16} color={colors.status.error.main} />
              <Text variant="body" color={colors.status.error.main}>
                Delete Rule
              </Text>
            </HStack>
          </Button>
        )}

        <Button
          variant="primary"
          onPress={handleSave}
          disabled={!selectedTemplateId || isSaving}
          style={{ flex: rule ? undefined : 1 }}
        >
          <HStack spacing="sm" align="center">
            {isSaving ? (
              <ActivityIndicator size="small" color={colors.primary[900]} />
            ) : (
              <Save size={16} color={colors.primary[900]} />
            )}
            <Text variant="body" color={colors.primary[900]} style={{ fontWeight: '600' }}>
              {rule ? 'Save Changes' : 'Create Auto-Call Rule'}
            </Text>
          </HStack>
        </Button>
      </HStack>

      {hasChanges && (
        <Box
          backgroundColor={colors.primary.faint}
          borderRadius="md"
          padding="sm"
          alignItems="center"
        >
          <Text variant="caption" color={colors.primary.main}>
            You have unsaved changes
          </Text>
        </Box>
      )}
    </VStack>
  );
}

const styles = StyleSheet.create({
  dayChip: {
    width: 44,
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
